/**
 * Shared utility functions — framework-agnostic.
 * Works in web, React Native, and Node.js environments.
 */

import type { LockedMaterialPrice, OrgCurrency, OrgUnits, QuoteType, ResidentialItemType } from './types';

/** Joins class names, filtering out falsy values */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/** Format a date to a readable locale string */
export function formatDate(date: Date | string, locale = 'en-US'): string {
  return new Date(date).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Truncate a string to a maximum length */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 1)}…`;
}

/** Format distanceMeters into a human-readable string based on org units. */
export function formatDistance(meters: number, units: OrgUnits = 'imperial'): string {
  if (units === 'metric') {
    const km = meters / 1000;
    return `${km.toFixed(1)} km`;
  }
  const miles = meters * 0.000621371;
  return `${miles.toFixed(1)} mi`;
}

/** Format durationSeconds into a human-readable string. */
export function formatDuration(seconds: number): string {
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)} min`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
}

/** Return the currency symbol for an org's default currency. */
export function currencySymbol(currency?: OrgCurrency): string {
  if (currency === 'EUR') return '€';
  if (currency === 'GBP') return '£';
  return '$';
}

/** Format an ISO date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm) for display.
 *  Shows time only when the value includes a time component. */
export function formatMoveDate(iso: string): string {
  const hasTime = iso.includes('T');
  const d = hasTime ? new Date(iso) : new Date(`${iso}T00:00`);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (!hasTime) return dateStr;
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${dateStr} at ${timeStr}`;
}

/** Password validation rules: 8+ chars, at least 1 letter, at least 1 number. */
export interface PasswordCheck {
  label: string;
  met: boolean;
}

export function getPasswordChecks(password: string): PasswordCheck[] {
  return [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains a letter', met: /[a-zA-Z]/.test(password) },
    { label: 'Contains a number', met: /\d/.test(password) },
  ];
}

export function isPasswordValid(password: string): boolean {
  return getPasswordChecks(password).every(c => c.met);
}

// ── Inventory pricing (packing items and equipment) ────────────────────────

/** Item categories that carry a unit cost and rental length. */
export const PRICED_ITEM_CATEGORIES: readonly ResidentialItemType[] = ['packing_item', 'equipment'];

export function isPricedCategory(category: ResidentialItemType | '' | undefined | null): boolean {
  return !!category && PRICED_ITEM_CATEGORIES.includes(category);
}

/**
 * Supplies that add no volume: a Packing Item or Equipment with a KZ of 0
 * (tape, bubble wrap, Masonite, door jamb protectors, …). They are added from
 * the Materials page and kept out of the Casegoods "Add Items" list. Customer
 * Items with KZ 0 (Painting, Computer Components) are real inventory and stay.
 */
export function isZeroVolumeMaterial(item: { residentialType?: ResidentialItemType; kz?: number | null }): boolean {
  return isPricedCategory(item.residentialType) && !((item.kz ?? 0) > 0);
}

/**
 * Summarise an item's pricing for display:
 *   "$3.00 per 10 days"  (rental)
 *   "$3.00 per day"      (1-day rental)
 *   "$26.00"             (no rental length: purchased or consumed)
 * Returns null when there is no unit cost to show.
 */
export function formatUnitPricing(
  unitCost: number | null | undefined,
  rentalDays: number | null | undefined,
): string | null {
  if (unitCost == null || !Number.isFinite(unitCost)) return null;
  const cost = `$${unitCost.toFixed(2)}`;
  if (rentalDays == null || rentalDays < 1) return cost;
  return rentalDays === 1 ? `${cost} per day` : `${cost} per ${rentalDays} days`;
}

/** Rental length a move's materials default to when the estimator has not set one. */
export const DEFAULT_MATERIALS_RENTAL_DAYS = 1;

/**
 * Whole rental periods billed for a job. An item rented per `rentalDays` on a
 * `jobRentalDays` job is charged full periods, never prorated: $5 per 3 days on
 * a 5-day job is 2 periods ($10). Items without a rental length are 1 period.
 */
export function rentalPeriodsForJob(
  rentalDays: number | null | undefined,
  jobRentalDays: number | null | undefined,
): number {
  if (rentalDays == null || rentalDays < 1) return 1;
  const job = jobRentalDays != null && jobRentalDays >= 1 ? Math.floor(jobRentalDays) : DEFAULT_MATERIALS_RENTAL_DAYS;
  return Math.max(1, Math.ceil(job / rentalDays));
}

/**
 * Cost of a material line on a move: quantity × unit cost × rental periods.
 * Returns null when the item has no unit cost, so callers can show "—" and
 * leave it out of totals.
 */
export function materialLineCost(
  unitCost: number | null | undefined,
  rentalDays: number | null | undefined,
  quantity: number,
  jobRentalDays: number | null | undefined,
): number | null {
  if (unitCost == null || !Number.isFinite(unitCost) || quantity <= 0) return null;
  const cents = Math.round(unitCost * 100) * rentalPeriodsForJob(rentalDays, jobRentalDays) * quantity;
  return cents / 100;
}

// ── Move materials ─────────────────────────────────────────────────────────
// Packing items and equipment on a move, with what they cost for the move's
// rental length. Used by the Materials page, the formula engine (the total is
// a line on the quote subtotal) and quote versions, so all three agree.

/** The quote fields the materials rollup reads. Structural so web / backend copies of Quote fit. */
export interface MaterialsQuoteInput {
  type?: QuoteType;
  casegoodsInventory?: ReadonlyArray<{ itemId: string; itemName: string; quantity: number; itemCategory?: ResidentialItemType }>;
  residentialRooms?: ReadonlyArray<{ items: ReadonlyArray<{ itemId: string; itemName: string; quantity: number; itemCategory?: ResidentialItemType }> }>;
  materialsRentalDays?: number;
  /** Prices locked into the quote; they win over the catalog's current price. */
  materialPrices?: Readonly<Record<string, LockedMaterialPrice>>;
}

/** The inventory fields the materials rollup reads. */
export interface MaterialsItemInput {
  id: string;
  residentialType?: ResidentialItemType | null;
  unitCost?: number | null;
  rentalDays?: number | null;
}

export type MaterialCategory = 'packing_item' | 'equipment';

export interface MaterialLine {
  itemId: string;
  itemName: string;
  /** Summed across rooms / duplicate lines. */
  quantity: number;
  category: MaterialCategory;
  unitCost?: number;
  rentalDays?: number;
  /** Rental periods billed for this move (1 for purchased items). */
  periods: number;
  /** quantity × unitCost × periods, or null when the item has no unit cost. */
  lineTotal: number | null;
}

export interface QuoteMaterialsSummary {
  lines: MaterialLine[];
  /** Sum of priced lines, in dollars. */
  total: number;
  /** Every material (sum of quantities), including $0 equipment. */
  count: number;
  /**
   * Sum of quantities over the client-listed lines (see isClientListedMaterial):
   * packing items plus priced equipment. This is the "N" in "Materials (×N)"
   * and the "N items" count everywhere the estimator or customer sees one.
   */
  listedCount: number;
  pricedCount: number;
  unpricedCount: number;
}

function isMaterialCategory(cat: ResidentialItemType | '' | null | undefined): cat is MaterialCategory {
  return cat === 'packing_item' || cat === 'equipment';
}

/**
 * Roll up a move's packing items and equipment. Commercial moves read
 * `casegoodsInventory`; residential moves read every room's items plus
 * `casegoodsInventory`, where materials added from the Materials page live
 * (they have no room). Quantities are summed per item; items whose category
 * is not Packing Item / Equipment are ignored. Lines are sorted by name.
 */
export function computeQuoteMaterials(
  quote: MaterialsQuoteInput | null | undefined,
  orgItems: ReadonlyArray<MaterialsItemInput>,
): QuoteMaterialsSummary {
  const empty: QuoteMaterialsSummary = { lines: [], total: 0, count: 0, listedCount: 0, pricedCount: 0, unpricedCount: 0 };
  if (!quote) return empty;
  const itemMap = new Map(orgItems.map(i => [i.id, i]));
  const lineItems = quote.type === 'residential'
    ? [...(quote.residentialRooms ?? []).flatMap(room => room.items), ...(quote.casegoodsInventory ?? [])]
    : (quote.casegoodsInventory ?? []);
  const jobRentalDays = quote.materialsRentalDays ?? DEFAULT_MATERIALS_RENTAL_DAYS;

  const byId = new Map<string, MaterialLine>();
  for (const li of lineItems) {
    if (li.quantity <= 0) continue;
    const orgItem = itemMap.get(li.itemId);
    const category = li.itemCategory ?? orgItem?.residentialType;
    if (!isMaterialCategory(category)) continue;
    const existing = byId.get(li.itemId);
    if (existing) {
      existing.quantity += li.quantity;
    } else {
      const price = quote.materialPrices?.[li.itemId] ?? orgItem;
      byId.set(li.itemId, {
        itemId: li.itemId,
        itemName: li.itemName,
        quantity: li.quantity,
        category,
        unitCost: price?.unitCost ?? undefined,
        rentalDays: price?.rentalDays ?? undefined,
        periods: 1,
        lineTotal: null,
      });
    }
  }

  const lines = Array.from(byId.values()).sort((a, b) => a.itemName.localeCompare(b.itemName));
  let total = 0, count = 0, listedCount = 0, pricedCount = 0, unpricedCount = 0;
  for (const line of lines) {
    line.periods = line.unitCost != null ? rentalPeriodsForJob(line.rentalDays, jobRentalDays) : 1;
    line.lineTotal = materialLineCost(line.unitCost, line.rentalDays, line.quantity, jobRentalDays);
    count += line.quantity;
    if (isClientListedMaterial(line)) listedCount += line.quantity;
    if (line.lineTotal == null) { unpricedCount++; continue; }
    pricedCount++;
    total += line.lineTotal;
  }
  return { lines, total: Math.round(total * 100) / 100, count, listedCount, pricedCount, unpricedCount };
}

/**
 * Lock the catalog price of every material on the quote that isn't locked yet,
 * the way `crew` locks hourly rates: the first price a material is quoted at
 * stays with the quote even if the admin edits the catalog later. Returns the
 * full map when something was added, or null when nothing changed.
 */
export function lockMaterialPrices(
  quote: MaterialsQuoteInput | null | undefined,
  orgItems: ReadonlyArray<MaterialsItemInput>,
): Record<string, LockedMaterialPrice> | null {
  if (!quote) return null;
  const itemMap = new Map(orgItems.map(i => [i.id, i]));
  const lineItems = quote.type === 'residential'
    ? [...(quote.residentialRooms ?? []).flatMap(room => room.items), ...(quote.casegoodsInventory ?? [])]
    : (quote.casegoodsInventory ?? []);
  const locked: Record<string, LockedMaterialPrice> = { ...(quote.materialPrices ?? {}) };
  let changed = false;
  for (const li of lineItems) {
    if (li.quantity <= 0 || locked[li.itemId]) continue;
    const orgItem = itemMap.get(li.itemId);
    // Unknown items (deleted from the catalog) stay unlocked: there is no price to copy.
    if (!orgItem || !isMaterialCategory(li.itemCategory ?? orgItem.residentialType)) continue;
    locked[li.itemId] = { unitCost: orgItem.unitCost ?? null, rentalDays: orgItem.rentalDays ?? null };
    changed = true;
  }
  return changed ? locked : null;
}

/**
 * Whether a material belongs on the client-facing materials list (quote
 * versions, PDF): every packing item, and equipment only when it has a unit
 * cost. Prices themselves are never listed per item.
 */
export function isClientListedMaterial(line: Pick<MaterialLine, 'category' | 'unitCost'>): boolean {
  return line.category === 'packing_item' || line.unitCost != null;
}

/** Parse a unit-cost form field into the value the API expects: a number, or null to clear. */
export function parseUnitCostInput(value: string): number | null {
  const trimmed = value.replace(/[$,\s]/g, '');
  if (!trimmed) return null;
  const num = parseFloat(trimmed);
  return Number.isFinite(num) && num >= 0 ? Math.round(num * 100) / 100 : null;
}

/** Parse a rental-length form field into whole days, or null to clear. */
export function parseRentalDaysInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = parseInt(trimmed, 10);
  return Number.isInteger(num) && num >= 1 ? num : null;
}
